import { IImage } from './IImage';

export class BrowserImage implements IImage {
    private image: HTMLImageElement;
    private loaded: Promise<void>;

    constructor(src?: string) {
        this.image = new Image();
        this.loaded = new Promise<void>((resolve, reject) => {
            this.image.onload = () => {
                resolve();
            };
            this.image.onerror = (e) => reject(e);
        });

        if (src) {
            this.image.src = src;
        }
    }

    private createCanvas(
        width: number,
        height: number,
    ): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to get canvas context');
        }

        return { canvas, ctx };
    }

    public async getJPegDataUri(quality = 0.8): Promise<string> {
        await this.loaded;

        const { canvas, ctx } = this.createCanvas(this.image.width, this.image.height);
        ctx.drawImage(this.image, 0, 0);
        return canvas.toDataURL('image/jpeg', quality);
    }

    public async getPngDataUri(): Promise<string> {
        await this.loaded;
        const { canvas, ctx } = this.createCanvas(this.image.width, this.image.height);
        ctx.drawImage(this.image, 0, 0);
        return canvas.toDataURL('image/png');
    }

    public async getDimensions(): Promise<{ width: number; height: number }> {
        await this.loaded;
        return {
            width: this.image.width,
            height: this.image.height,
        };
    }

    public async crop(x: number, y: number, width: number, height: number): Promise<IImage> {
        await this.loaded;

        const { canvas, ctx } = this.createCanvas(width, height);
        ctx.drawImage(this.image, x, y, width, height, 0, 0, width, height);

        return new BrowserImage(canvas.toDataURL());
    }

    public async fit(width: number, height: number): Promise<IImage> {
        await this.loaded;

        const aspectRatio = this.image.width / this.image.height;

        let newWidth = width;
        let newHeight = height;

        if (width / height > aspectRatio) {
            newWidth = height * aspectRatio;
        } else {
            newHeight = width / aspectRatio;
        }

        const { canvas, ctx } = this.createCanvas(newWidth, newHeight);

        ctx.drawImage(this.image, 0, 0, newWidth, newHeight);

        return new BrowserImage(canvas.toDataURL());
    }
}
